/* eslint-disable react/no-this-in-sfc */
import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Link } from '@techstyle/next-routes';
import {
  AssetContainer,
  HTMLAsset,
  ImageAsset,
  useAsset,
} from '@techstyle/react-assets';
import { Button, useBreakpoint, useTheme } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { HERO_CAROUSEL_HEIGHT } from '../../constants/fullScreenCarousel';
import { desktop } from '../../styles';
import { createMembershipPromoPriceAssetFilter } from '../../utils/assetFilters';
import { Container as AssetCountdown } from '../AssetCountdown';
import { Component as BackgroundVideo } from '../BackgroundVideo';
import { Component as BorderfreeGuard } from '../BorderfreeGuard';
import { Component as Carousel } from '../Carousel';
import { Component as CarouselDotButton } from '../CarouselDotButton';
import { EnterViewport } from '../EnterViewport';
import { Component as StaticIcon } from '../StaticIcon';
import { Component as VideoCarouselItem } from '../VideoCarouselItem';

const caretSize = 50;

const CarouselWrapper = styled.div`
  background-color: ${props => props.theme.colors.white};
  position: relative;
`;

// testing for homepage carousel only
const StyledCarousel = styled(Carousel)`
  height: 100%;
  width: 100%;

  /* Carousel overrides to get a height of 100vh */
  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100%;
  }

  .slick-active {
    button:before {
      background-color: ${props => props.theme.colors.white} !important;
      box-shadow: none;
    }
  }

  .slick-dots {
    z-index: ${props => props.theme.zIndex.increment};
    margin-bottom: 12px;
  }
`;

const StyledCarouselWithTextUnder = styled(StyledCarousel)`
  height: 100%;

  .slick-slide > div {
    height: 100%;
  }

  .slick-dots {
    bottom: calc(100% - ${HERO_CAROUSEL_HEIGHT});
    ${desktop`
       margin-bottom: 12px;
    `};
  }
`;

const GRID_GAP = 10;
const CarouselBanner = styled.div`
  margin-bottom: ${GRID_GAP * -1}px;
  padding: ${props => props.theme.spacing.moderate}px;
  text-align: center;

  font-size: 12px;
  ${desktop`
    padding: ${props => props.theme.spacing.moderate}px;
    font-size: 16px;
  `};
`;

const ContentWrapper = styled.div`
  height: ${props => (props.isHeroCarousel ? HERO_CAROUSEL_HEIGHT : '100%')};
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;

  align-items: ${props =>
    props.mobileTextPosition.includes('left')
      ? `flex-start`
      : props.mobileTextPosition.includes('right')
      ? `flex-end`
      : `center`};
  justify-content: ${props =>
    props.mobileTextPosition.includes('top')
      ? `flex-start`
      : props.mobileTextPosition.includes('bottom')
      ? `flex-end`
      : `center`};

  ${desktop`
    align-items: ${props =>
      props.desktopTextPosition.includes('left')
        ? `flex-start`
        : props.desktopTextPosition.includes('right')
        ? `flex-end`
        : `center`};
    justify-content: ${props =>
      props.desktopTextPosition.includes('top')
        ? `flex-start`
        : props.desktopTextPosition.includes('bottom')
        ? `flex-end`
        : `center`};
  `}
`;

const StyledHTMLAsset = styled(HTMLAsset)`
  padding: 0 ${props => props.theme.spacing.tiny}px;
  text-align: center;

  ${desktop`
    padding: 0 ${caretSize}px;
  `};
`;

/* A link that looks like a Button */
const StyledButtonLink = styled(Button).attrs(({ white = true }) => ({
  white,
}))`
  ${props => props.theme.button.defaultStyle};
  margin-top: ${props => props.theme.spacing.small}px;
  height: 40px;
  min-width: 200px;

  ${desktop`
    margin-top: ${props => props.theme.spacing.medium}px;
  `};
`;

const SlideWrapperElement = styled.div`
  display: block !important;
  position: relative;
  background-color: ${props => props.bgColor};
  ${({ hideOverflow }) => hideOverflow && `overflow: hidden;`};
  height: 100%;
`;

const Slide = styled(ImageAsset)`
  width: auto;
  height: 100%;

  background-color: ${props => props.bgColor};
  background-position: ${props => `${props.bgX} ${props.bgY}`};
  background-repeat: no-repeat;
`;

const BackgroundImageSlide = styled(Slide)`
  position: relative;
`;
const CarouselButton = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  overflow: hidden;
  z-index: ${props => props.theme.zIndex.increment};

  background: transparent;
  color: ${props => props.theme.colors.white};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: normal;
  line-height: 1.4;
  text-transform: uppercase;
  width: 50px;

  &:focus {
    outline: none;
  }
  /* Remove default dotted border on Firefox */
  &::-moz-focus-inner {
    border: 0;
  }
`;

const NextButton = styled(CarouselButton).attrs({
  [`aria-label`]: 'Next',
})`
  display: ${props => (props.hide ? 'none !important' : 'block')};
  right: 0;

  cursor: e-resize;
  text-align: right;
`;

const RightIcon = styled(StaticIcon)`
  transform: rotate(-90deg);
`;

const PrevButton = styled(CarouselButton).attrs({
  [`aria-label`]: 'Previous',
})`
  display: ${props => (props.hide ? 'none !important' : 'block')};
  left: 0;

  cursor: w-resize;
  text-align: left;
`;

const LeftIcon = styled(StaticIcon)`
  transform: rotate(90deg);
`;

const AutoplayVideoContentWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const TextUnderCarousel = styled.p`
  text-align: center;
  font-size: 12px;

  ${desktop`
    font-size: 16px;
  `};
`;

class FullScreenCarousel extends React.PureComponent {
  static SlideWrapper = SlideWrapperElement;
  static Slide = Slide;
  static StyledHTMLAsset = StyledHTMLAsset;
  static AssetCountdown = AssetCountdown;
  static propTypes = {
    className: PropTypes.string,
    combinedAssets: PropTypes.array,
    container: PropTypes.string.isRequired,
    countDownEndDate: PropTypes.string,
    includePauseButtonWithDots: PropTypes.func,
    isHeroCarousel: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    isMobile: PropTypes.bool,
    membershipPrice: PropTypes.number,
    // To add arrows to mobile carousel when slide is a youtube video
    needMobileArrows: PropTypes.bool,
    onAssetClick: PropTypes.func,
    onAssetEnterViewport: PropTypes.func,
    onCountdownComplete: PropTypes.func,
    options: PropTypes.object,
    pSource: PropTypes.string,
    theme: PropTypes.object,
    trackHomepageCarousel: PropTypes.func,
    videoHeight: PropTypes.string,
    videoOptions: PropTypes.object,
  };

  static defaultProps = {
    container: null,
    options: {},
    isLoggedIn: false,
    isHeroCarousel: false,
    needMobileArrows: false,
    onCountdownComplete: () => {},
    pSource: null,
    trackHomepageCarousel: () => {},
    videoOptions: {
      config: {},
      controls: true,
      loop: false,
      playing: false,
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      playing: props.videoOptions.playing,
      currentSlide: 1,
    };
  }

  updateSliderDimensions = node => {
    if (!node) {
      return;
    }
    this.setState({
      sliderDimensions: [
        node.getBoundingClientRect().width,
        node.getBoundingClientRect().height,
      ],
    });
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { pSource, trackHomepageCarousel, isLoggedIn } = this.props;
    const { currentSlide } = this.state;

    if (
      currentSlide !== prevState.currentSlide &&
      pSource === 'homepage' &&
      isLoggedIn
    ) {
      trackHomepageCarousel(currentSlide);
    }
  };

  delay(timeInMs) {
    return new Promise(resolve => setTimeout(() => resolve(), timeInMs));
  }

  handlePauseCarousel = shouldAutoplayCarousel => {
    if (shouldAutoplayCarousel) {
      this.slider.slickPause();
    }
  };

  handlePlayCarousel = shouldAutoplayCarousel => {
    if (shouldAutoplayCarousel && this.slider) {
      this.slider.slickPlay();
    }
  };

  setSliderNode = el => {
    this.slider = el;
  };

  renderCta(currentAsset) {
    const {
      options: {
        customVars: { ctaText, ctaTheme = '' },
        ...options
      },
    } = currentAsset;

    if (options.targetUrl) {
      return (
        <Link route={options.targetUrl} passHref>
          <StyledButtonLink
            alternate={ctaTheme.includes('alternate')}
            secondary={ctaTheme.includes('secondary')}
            white={ctaTheme.includes('white')}
          >
            {ctaText || (
              <FormattedMessage
                defaultMessage="Shop now"
                id="site_home.shop_now_home_page"
              />
            )}
          </StyledButtonLink>
        </Link>
      );
    }
    return null;
  }

  /**
   * this has been added to handle react-slick infinite scroll + intersection observer.
   * When scrolling back and forth through the carousel, react-slick has ghost slides it will show
   * the user to make it seem like the carousel is scrolling forever. This behavior causes both the
   * intersection observer for the ghost slide + regular slide to fire. 'carouselObserverIntersectionFired'
   * keeps track of what slide index has fired the event - and will only fire once rather than 2x.
   */
  carouselObserverIntersectionFired = {};

  renderSlides(assets, shouldAutoplayCarousel = true) {
    const {
      countDownEndDate,
      onAssetClick,
      onAssetEnterViewport,
      onCountdownComplete,
      isHeroCarousel,
      videoHeight,
      videoOptions,
    } = this.props;

    return assets.map((asset, index) => {
      const { id, options, imageFilename } = asset;
      const { customVars } = options;
      const key = `Slide-${id}`;

      const { controls, muted, loop, style, playing } = videoOptions;
      const showVideoControls = controls;

      const maintainAspectRatio = !!(
        customVars.imagePosition &&
        customVars.imagePosition === 'backgroundFull'
      );

      const mobileTextPosition = customVars.textPosition || 'bottom';
      const desktopTextPosition = customVars.textPosition || 'center';

      const SlideWrapper = ({ children, ...props }) => {
        const onClickAsset = () => onAssetClick && onAssetClick(asset);

        return (
          <EnterViewport
            onEnterViewport={() => {
              if (!onAssetEnterViewport) {
                return;
              }

              const hasSlideEnterViewportFired =
                this.carouselObserverIntersectionFired[index];

              if (!hasSlideEnterViewportFired) {
                this.carouselObserverIntersectionFired[index] = true;
                onAssetEnterViewport && onAssetEnterViewport(assets[index]);
              }
            }}
          >
            <SlideWrapperElement
              {...props}
              data-testid={key}
              onClick={onClickAsset}
            >
              {children}
            </SlideWrapperElement>
          </EnterViewport>
        );
      };

      const defaultContent = (
        <ContentWrapper
          mobileTextPosition={mobileTextPosition}
          desktopTextPosition={desktopTextPosition}
          isHeroCarousel={isHeroCarousel}
        >
          {options.htmlText ? <StyledHTMLAsset asset={asset} /> : null}
          {this.renderCta(asset)}
        </ContentWrapper>
      );

      const textUnderCarousel = customVars.textUnderCarousel ? (
        <BorderfreeGuard>
          <CarouselBanner>
            <TextUnderCarousel>
              {customVars.textUnderCarousel}
            </TextUnderCarousel>
          </CarouselBanner>
        </BorderfreeGuard>
      ) : null;

      // The asset tool does not differentiate between different types of uploaded files
      // so use a custom variable to know that a video file (.mp4) has been uploaded
      // FIXME: do we need this anymore? We generally do not upload videos via CMS anymore
      if (customVars.isVideo) {
        return (
          <SlideWrapper key={key} hideOverflow>
            <BackgroundVideo
              autoplay={playing}
              controls={controls}
              loop={loop}
              muted={muted}
              onClick={onAssetClick}
              src={imageFilename}
            />
          </SlideWrapper>
        );
      }

      // `videoUrl` and `videoUrlPortrait` are used for videos hosted
      // on Vimeo/YouTube/etc (third party services)
      if (customVars.videoUrl) {
        const videoPlayer = (
          <VideoCarouselItem
            asset={asset}
            onEnded={() => this.handlePlayCarousel(shouldAutoplayCarousel)}
            onPause={() => this.handlePlayCarousel(shouldAutoplayCarousel)}
            onPlay={() => this.handlePauseCarousel(shouldAutoplayCarousel)}
            onClick={onAssetClick}
            playing={showVideoControls ? playing : this.state.playing}
            playsinline={isHeroCarousel}
            style={style}
            shouldLoopInBackground={isHeroCarousel}
            videoFitDimensions={this.state.sliderDimensions}
            videoHeight={videoHeight}
            videoOptions={videoOptions}
          />
        );

        if (!showVideoControls) {
          return (
            <>
              <SlideWrapper key={key} ref={this.updateSliderDimensions}>
                {videoPlayer}
                <AutoplayVideoContentWrapper>
                  {defaultContent}
                </AutoplayVideoContentWrapper>
              </SlideWrapper>
              {textUnderCarousel}
            </>
          );
        }

        return <SlideWrapper key={key}>{videoPlayer}</SlideWrapper>;
      }

      if (customVars.countDownEndDate || customVars.countDownTimer) {
        const endDate = customVars.countDownEndDate
          ? customVars.countDownEndDate
          : countDownEndDate;

        const countDownTimer = (
          <AssetCountdown
            asset={asset}
            countDownEndDate={endDate}
            ctaText={customVars.ctaText}
            ctaUrl={options.targetUrl}
            ctaTheme={customVars.ctaTheme}
            onCountdownComplete={onCountdownComplete}
            onCtaClick={onAssetClick}
          />
        );

        if (imageFilename) {
          // Passing `...asset` is a bad API because assets have fields that
          // conflict with both React and HTML, like `key` and `id`. So remove
          // those before passing the rest.
          // TODO: migrate to react-assets so we can also remove `id` prop.
          const { key } = asset;
          return (
            <SlideWrapper key={key}>
              <BackgroundImageSlide
                asset={asset}
                bgX={customVars.horizontalFocus || 'center'}
                bgY={customVars.verticalFocus || 'center'}
                bgColor={options.bgColor || 'transparent'}
                background
                key={key}
              >
                {countDownTimer}
              </BackgroundImageSlide>
              {textUnderCarousel}
            </SlideWrapper>
          );
        }

        return (
          <SlideWrapper key={key} bgColor={options.bgColor || 'transparent'}>
            {countDownTimer}
          </SlideWrapper>
        );
      }

      if (imageFilename) {
        // Passing `...asset` is a bad API because assets have fields that
        // conflict with both React and HTML, like `key` and `id`. So remove
        // those before passing the rest.
        // TODO: migrate to react-assets so we can also remove `id` prop.
        const { key } = asset;
        return (
          <SlideWrapper key={key}>
            <BackgroundImageSlide
              background
              maintainAspectRatio={maintainAspectRatio}
              bgColor={options.bgColor || 'transparent'}
              bgX={customVars.horizontalFocus || 'center'}
              bgY={customVars.verticalFocus || 'center'}
              asset={asset}
            >
              {defaultContent}
            </BackgroundImageSlide>
            {textUnderCarousel}
          </SlideWrapper>
        );
      }

      return (
        <SlideWrapper key={key} bgColor={options.bgColor || 'transparent'}>
          {defaultContent}
        </SlideWrapper>
      );
    });
  }

  setSlideIndex = currentSlide => {
    this.setState({ currentSlide });
  };

  addPauseButton = (options, numberOfSlides) => {
    const carouselRef = this.slider;

    if (this.props.includePauseButtonWithDots) {
      options.appendDots = this.props.includePauseButtonWithDots({
        carouselRef,
        numberOfSlides,
        setSlideIndex: this.setSlideIndex,
        slideIndex: this.state.currentSlide,
      });

      if (options.autoplay) {
        options.autoplay = false;
      }
    }

    return options;
  };

  render() {
    const {
      theme,
      container,
      className,
      isMobile,
      options,
      combinedAssets,
      needMobileArrows,
      membershipPrice,
      ...rest
    } = this.props;

    const defaultOptions = {
      adaptiveHeight: false,
      autoplaySpeed: 5000,
      customPaging: i => (
        <CarouselDotButton key={`dots-${i}`} index={i} dotColor="white" />
      ),
      dots: true,
      infinite: true,
      nextArrow: (
        <NextButton>
          <RightIcon type="caret" size={caretSize} />
        </NextButton>
      ),
      prevArrow: (
        <PrevButton>
          <LeftIcon type="caret" size={caretSize} />
        </PrevButton>
      ),
      slidesToShow: 1,
      speed: 500,
      useTransform: false,
      responsive: [
        {
          breakpoint: theme.breakpoints.large - 1,
          settings: {
            arrows: needMobileArrows,
          },
        },
      ],
      afterChange: current => this.setState({ currentSlide: current + 1 }),
      beforeChange: (oldIndex, newIndex) => {
        // used only for intersection observer for asset entering view
        if (!this.props.onAssetEnterViewport) {
          return;
        }

        this.carouselObserverIntersectionFired[oldIndex] = false;
        this.carouselObserverIntersectionFired[newIndex] = false;
      },
    };

    if (combinedAssets) {
      const combinedAssetOptions = { ...defaultOptions, ...options };

      return (
        <CarouselWrapper className={className} {...rest}>
          <StyledCarousel
            sliderRef={this.setSliderNode}
            options={this.addPauseButton(
              combinedAssetOptions,
              combinedAssets.length
            )}
          >
            {this.renderSlides(combinedAssets)}
          </StyledCarousel>
        </CarouselWrapper>
      );
    }

    return (
      <CarouselWrapper className={className} {...rest}>
        <AssetContainer
          name={container}
          multi
          filter={createMembershipPromoPriceAssetFilter(membershipPrice)}
        >
          {assets => {
            if (!assets || !assets.data) {
              return null;
            }

            const hasTextUnderCarousel = assets?.data?.some(
              ({ options }) => options.customVars.textUnderCarousel
            );

            const CarouselContainer = hasTextUnderCarousel
              ? StyledCarouselWithTextUnder
              : StyledCarousel;

            const customVars = assets.data.map(
              asset => asset.options.customVars
            );

            // if there is no autoplay in customVar, then autoplay by default
            // if there is, it should autoplay as long as there is one autoplay true in assets
            const someAssetsHaveAutoplay = customVars.some(
              customVar =>
                !Object.prototype.hasOwnProperty.call(customVar, 'autoplay') ||
                customVar.autoplay
            );

            let carouselOptions = {
              ...defaultOptions,
              ...options,
              // if builderAutoplay is present, we use that value instead of assets customVars
              autoplay: options.builderAutoplay ?? someAssetsHaveAutoplay,
              afterChange: current => {
                if (!this.props.includePauseButtonWithDots) {
                  this.setState({ currentSlide: current + 1 });
                }
              },
            };

            if (someAssetsHaveAutoplay) {
              carouselOptions = this.addPauseButton(
                carouselOptions,
                assets.data.length
              );
            }

            const slides = this.renderSlides(
              assets.data,
              someAssetsHaveAutoplay
            );

            return (
              <CarouselContainer
                isMobile={isMobile}
                options={carouselOptions}
                sliderRef={this.setSliderNode}
              >
                {slides}
              </CarouselContainer>
            );
          }}
        </AssetContainer>
      </CarouselWrapper>
    );
  }
}

FullScreenCarousel.BackgroundImageSlide = BackgroundImageSlide;

const FullScreenCarouselWithHooks = ({ onEnterViewport, ...props }) => {
  const { mobile } = useBreakpoint();
  const theme = useTheme();
  const assetContainer = useAsset(props.container, { multi: true });
  const asset = assetContainer?.data?.assets?.[0];

  return (
    <EnterViewport
      once
      onEnterViewport={onEnterViewport && onEnterViewport.bind(null, asset)}
    >
      <FullScreenCarousel isMobile={mobile} theme={theme} {...props} />
    </EnterViewport>
  );
};

FullScreenCarouselWithHooks.propTypes = {
  container: PropTypes.string.isRequired,
  onEnterViewport: PropTypes.func,
  onAssetEnterViewport: PropTypes.func,
};

export default FullScreenCarouselWithHooks;
